import { hasTruthyProperties } from '../helpers/object.helpers';
import { getProductIdsInRecipes } from '../helpers/recipe.helpers';
import { flattenElements } from '../helpers/recursive-element.helpers';
import { IProduct, ProductID, ProductRecord } from '../models/product.model';
import { Recipe } from '../models/recipe.model';
import { throwValidationErrors } from './validation.helpers';
import { isValidElements } from './project-validation';
import { Project } from '../models/project.model';
import { TMP_PROJECT_ID } from '../constants';
import {
  getMissingActionRecipeApplyProductIdsInVersion,
  getProjectProductsRecord,
  toProductIds,
} from '../helpers/product.helpers';
import { getAllBuildingVersions } from '../helpers/recursive-element.helpers';
import { uniq } from 'lodash';
import { genericProductsLookup } from '../generic-products';
import { getElementCategoryById } from '../templates/categories';
import { GenericValidationError, ValidationResult } from './validation.models';
import { isValidOwnerAndOrganizations } from './organization.validation';

/** Ids of bundled generic products, available in every org/project (computed once). */
const genericProductIds = new Set(toProductIds(genericProductsLookup));

export const validateRecipe = (
  recipe: Recipe,
  productRecord?: ProductRecord,
  onError?: (error: string) => void,
): Recipe => {
  throwValidationErrors(
    isValidRecipe(recipe, productRecord),
    'RecipeValidationError',
    onError,
  );
  return recipe;
};

// TODO: Make sure that ALL products are available when validating recipes
export const isValidRecipe = (
  recipe: Recipe,
  productRecord:
    ProductRecord | IProduct[] | ProductID[] = genericProductsLookup,
): ValidationResult => {
  const { category_id, properties } = recipe;

  if (!getElementCategoryById(category_id, false)) {
    return new GenericValidationError(
      `Recipe refers to missing category: "${category_id}"`,
    );
  }
  if (!properties) {
    return new GenericValidationError('Recipe properties are missing');
  }
  if (!recipe.elements) {
    return new GenericValidationError('Recipe elements are missing');
  }

  // Make sure old recipe format is not allowed to be saved
  if (!Array.isArray(recipe.elements)) {
    return new GenericValidationError('Elements must be an array');
  }
  if (!hasTruthyProperties(recipe, 'id', 'name')) {
    return new GenericValidationError('Elements is missing name or id');
  }
  // Generic products (bundled static data, merged into every version during enrichment)
  // are always resolvable, so they count as available regardless of the org product
  // record the caller passes (e.g. client save-as passes org-only products). Without
  // this a recipe referencing a generic/Boverket product fails save validation even
  // though it resolves fine everywhere else.
  const prodIdsInRecipe = getProductIdsInRecipes(recipe);
  const availableProdIds = new Set([
    ...toProductIds(productRecord),
    ...genericProductIds,
  ]);
  const missing = prodIdsInRecipe.filter((id) => !availableProdIds.has(id));

  if (missing.length > 0) {
    return new GenericValidationError(
      `Missing product ids in recipe "${recipe.name}. Have you provided all products available in organization "${recipe.organizations.join(', ')}"?`,
    );
  }
  const elements = flattenElements(...recipe.elements);

  // TODO: Check that recipe doesn't have references to recipes in other organizations (other than template organization)

  if (!elements.length) {
    return new GenericValidationError(
      `Recipe "${recipe.name}" has no elements`,
    );
  }

  // Check that all elements in the recipe are valid
  const validElements = isValidElements(elements);
  if (validElements !== true) {
    return validElements;
  }

  // Check that the recipe has a valid owner and organization
  const validOwnerAndOrganizations = isValidOwnerAndOrganizations(recipe);
  if (validOwnerAndOrganizations !== true) {
    return validOwnerAndOrganizations;
  }

  return true;
};

export const validateRecipeProducts = (
  project: Project,
  usedRecipes: Recipe[],
): Project => {
  throwValidationErrors(isValidRecipeProducts(project, usedRecipes));
  return project;
};

/**
 * Make sure all products used in recipes are defined in the project.
 * @param project
 * @param usedRecipes
 * @returns
 */
export const isValidRecipeProducts = (
  project: Project,
  usedRecipes: Recipe[],
): ValidationResult => {
  const recipeProductIds = getProductIdsInRecipes(...usedRecipes);
  const productRecord = getProjectProductsRecord(project);
  const missingFromUsedRecipes = recipeProductIds.filter(
    (id) => !productRecord[id],
  );

  const missingFromActionApplies = getAllBuildingVersions(project).flatMap(
    (version) => getMissingActionRecipeApplyProductIdsInVersion(version),
  );

  const missing = uniq([
    ...missingFromUsedRecipes,
    ...missingFromActionApplies,
  ]);

  // Don't check for missing products in tmp project (used on import/copy+paste)
  if (project.owner !== TMP_PROJECT_ID && missing.length > 0) {
    return new GenericValidationError(
      `Missing product ids in recipe ${missing.join(', ')}`,
    );
  }
  return true;
};
